import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight, Eye, EyeOff, Save, User, Phone, Mail, Home, Shield, Building, RefreshCw, Loader, Check, X } from 'lucide-react';
import styles from './UsersManagement.module.css';
import { createUserWithBuilding, updateUserBuildingRole, getCurrentProfile, getUserBuildings } from '../../Fetch/fetchUser';
import { getListApartmensByBuildingId } from '../../Fetch/fetchapartment';

const UserForm = ({ user, onSave, onCancel }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [formData, setFormData] = useState({
    FullName: '',
    Email: '', // Will be auto-generated if empty
    Phone: '',
    PasswordHash: '', // Will be auto-generated
    IsManager: 'לא', // Default to 'לא' (No) for new users
    IsVaad: 'לא',
    IsTenant: 'כן', // Also default tenant to 'כן' for new users
    RelationshipType: 'שוכר',
    ApartmentNumber: '',
    FloorNumber: '',
    OwnerName: '', // New field for owner name when relationshipType is 'שוכר'
    OwnerPhone: '', // New field for owner phone when relationshipType is 'שוכר'
    TenantName: '', // New field for tenant name
    TenantPhone: '', // New field for tenant phone
    VaadMonthly: 0,
    BuildingId: null,
    ApartmentID: null,
    ParkingNumber: '', // Parking numbers as string (comma separated)
    ChargingStation: false // Charging station yes/no
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apartments, setApartments] = useState([]);
  const [isEmailGenerated, setIsEmailGenerated] = useState(false);
  const [isAutoFilling, setIsAutoFilling] = useState(false);
  const [previousEmail, setPreviousEmail] = useState('');
  
  // Aggressive autofill prevention for edit mode
  useEffect(() => {
    if (user) {
      const clearFieldsAggressively = () => {
        // Add multiple timeouts to catch different autofill timings
        const timeouts = [0, 50, 100, 200, 500, 1000];
        
        timeouts.forEach(delay => {
          setTimeout(() => {
            const emailField = document.querySelector('input[name="userEmail"]');
            const passwordField = document.querySelector('input[name="userPassword"]');
            
            // Only clear if email is temp email, not for regular user emails
            if (emailField && user.Email && user.Email.includes('@temp-vaad.com')) {
              emailField.value = '';
              console.log(`🔧 CLEARED temp email at ${delay}ms delay`);
            }
            
            // Only clear password for temp emails
            if (passwordField && user.Email && user.Email.includes('@temp-vaad.com')) {
              passwordField.value = '';
              console.log(`🔧 CLEARED password at ${delay}ms delay`);
            }
          }, delay);
        });
      };
      
      clearFieldsAggressively();
    }
  }, [user]);

  // Function to format phone number with dash
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, '');
    
    // Apply formatting based on length
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 10) {
      return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
    } else {
      // Limit to 10 digits maximum
      return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 10)}`;
    }
  };

  // Function to get clean phone number (digits only)
  const getCleanPhoneNumber = (formattedPhone) => {
    return formattedPhone.replace(/\D/g, '');
  };

  // Function to generate a random password
  const generatePassword = () => {
    const length = 8;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%";
    let password = "";
    
    // Ensure at least one of each type
    password += "abcdefghijklmnopqrstuvwxyz"[Math.floor(Math.random() * 26)]; // lowercase
    password += "ABCDEFGHIJKLMNOPQRSTUVWXYZ"[Math.floor(Math.random() * 26)]; // uppercase
    password += "0123456789"[Math.floor(Math.random() * 10)]; // number
    password += "!@#$%"[Math.floor(Math.random() * 5)]; // special char
    
    // Fill the rest randomly
    for (let i = 4; i < length; i++) {
      password += charset[Math.floor(Math.random() * charset.length)];
    }
    
    // Shuffle the password
    return password.split('').sort(() => Math.random() - 0.5).join('');
  };

  // Function to generate temporary email based on phone number
  const generateTempEmail = (phone) => {
    if (!phone || phone.trim() === '') {
      // Generate random email if no phone
      const randomId = Math.random().toString(36).substring(7);
      return `temp_user_${randomId}@temp-vaad.com`;
    }
    
    // Clean phone number (remove spaces, dashes, etc.)
    const cleanPhone = getCleanPhoneNumber(phone);
    return `temp_${cleanPhone}@temp-vaad.com`;
  };

  // Function to check if email is a generated temp email
  const isTempEmail = (email) => {
    return email && email.includes('@temp-vaad.com');
  };

  // Get current building ID from localStorage
  const getCurrentBuildingId = () => {
    const userData = JSON.parse(localStorage.getItem('user'));
    return userData?.buildingId;
  };

  // Initialize form data
  useEffect(() => {
    console.log('🚀 UserForm useEffect triggered with user:', user);
    console.log('🔍 Is user truthy?', !!user);
    
    if (user) {
      // Editing existing user - preserve original password hash for updates
      console.log('🔧 Editing user data received:', {
        UserID: user.UserID,
        FullName: user.FullName,
        Email: user.Email,
        Phone: user.Phone,
        allFields: Object.keys(user)
      });
      
      // Check if this looks like logged-in user data vs selected user data
      const loggedInUser = JSON.parse(localStorage.getItem('user') || '{}');
      console.log('⚖️ Comparison - Received user vs Logged-in user:', {
        receivedEmail: user.Email,
        loggedInEmail: loggedInUser.email,
        receivedUserID: user.UserID,
        loggedInUserID: loggedInUser.userId,
        areEmailsSame: user.Email === loggedInUser.email
      });
      
      // CRITICAL: If we're receiving logged-in user data instead of selected user, log an error
      if (user.Email === loggedInUser.email && user.UserID === loggedInUser.userId) {
        console.error('❌ CRITICAL: UserForm received logged-in user data instead of selected user!');
        console.error('This means the editingUser state in UsersManagement is incorrect');
      }
      
      // CRITICAL DEBUG: Check exactly what email we're receiving
      console.log('🔍 DETAILED EMAIL ANALYSIS:');
      console.log('user.Email (raw):', user.Email);
      console.log('user.Email type:', typeof user.Email);
      console.log('user.Email || "" result:', user.Email || '');
      console.log('Is user.Email empty string?', user.Email === '');
      console.log('Is user.Email null?', user.Email === null);
      console.log('Is user.Email undefined?', user.Email === undefined);
      console.log('Is user.Email falsy?', !user.Email);
      
      // CRITICAL FIX: Check if the email is the same as logged-in user's email (reuse loggedInUser from above)
      const loggedInEmail = loggedInUser.email;
      const isLoggedInEmail = user.Email === loggedInEmail;
      console.log('🚨 Email comparison - Is user email same as logged-in?', isLoggedInEmail);
      
      // ONLY FORCE EMPTY FOR TEMP EMAILS, NOT FOR LOGGED-IN USER
      // The previous logic was wrong - we should show the actual email when editing any user
      const isTempEmailValue = user.Email && user.Email.includes('@temp-vaad.com');
      const shouldForceEmpty = isTempEmailValue; // Remove the isLoggedInEmail check
      const finalEmail = shouldForceEmpty ? '' : (user.Email || '');
      
      console.log('🔍 Additional checks:', {
        isTempEmail: isTempEmailValue,
        shouldForceEmpty: shouldForceEmpty
      });
      console.log('✅ Final email to use:', finalEmail || '(empty)');
      
      setFormData({
        UserID: user.UserID,
        FullName: user.FullName || '',
        Email: finalEmail, // Use the cleaned email
        Phone: formatPhoneNumber(user.Phone || ''), // Format existing phone
        PasswordHash: user.PasswordHash || '', // Preserve original password hash
        IsManager: user.IsManager || 'לא',
        IsVaad: user.IsVaad || 'לא',
        IsTenant: user.IsTenant || 'לא',
        RelationshipType: user.RelationshipType || 'שוכר',
        ParkingNumber: user.ParkingNumber || '',
        ChargingStation: user.ChargingStation || false,
        ApartmentNumber: user.ApartmentNumber || '',
        FloorNumber: user.FloorNumber || '',
        OwnerName: user.OwnerName || '',
        OwnerPhone: user.OwnerPhone || '',
        TenantName: user.TenantName || '',
        TenantPhone: user.TenantPhone || '',
        VaadMonthly: user.VaadMonthly || 0,
        // Make sure BuildingId is set - try user's BuildingId first, then fall back to localStorage
        BuildingId: user.BuildingId || getCurrentBuildingId(),
        ApartmentID: user.ApartmentID || null
      });
      
      // AGGRESSIVE AUTOFILL PREVENTION: Force clear fields after state update
      setTimeout(() => {
        const emailInput = document.querySelector('input[name="userEmail"]');
        const passwordInput = document.querySelector('input[name="userPassword"]');
        
        if (emailInput && shouldForceEmpty) {
          emailInput.value = '';
          console.log('🔧 FORCE CLEARED email field via DOM');
        }
        if (passwordInput && shouldForceEmpty) {
          passwordInput.value = '';
          console.log('🔧 FORCE CLEARED password field via DOM');
        }
      }, 100);
      
      console.log('✅ Form data set with email:', finalEmail || '(empty)');
      setIsEmailGenerated(isTempEmail(finalEmail));
      console.log('✅ Form data initialized for editing:', {
        originalEmail: user.Email,
        finalEmail: finalEmail,
        phone: user.Phone,
        isEmailGenerated: isTempEmail(finalEmail)
      });
      
      // AGGRESSIVE FIX: Force clear fields after a short delay to override any browser auto-fill
      setTimeout(() => {
        if (finalEmail === '') {
          console.log('🔧 AGGRESSIVE FIX: Force clearing email field after delay');
          setFormData(prev => ({
            ...prev,
            Email: ''
          }));
        }
      }, 100);
    } else {
      // Creating new user
      const userData = JSON.parse(localStorage.getItem('user'));
      const generatedPassword = generatePassword();
      
      // Reset previous email for auto-fill
      setPreviousEmail('');
      
      setFormData({
        FullName: '',
        Email: '', // Start empty - will generate when phone is entered
        Phone: '',
        PasswordHash: generatedPassword,
        IsManager: 'לא', // Default to 'לא' (No) for new users
        IsVaad: 'לא',
        IsTenant: 'כן', // Default to 'כן' (Yes) for new users
        RelationshipType: 'שוכר',
        ApartmentNumber: '',
        FloorNumber: '',
        OwnerName: '',
        OwnerPhone: '',
        TenantName: '',
        TenantPhone: '',
        VaadMonthly: 0,
        BuildingId: userData?.buildingId,
        ApartmentID: null,
        ParkingNumber: '',
        ChargingStation: false
      });
      setIsEmailGenerated(false);
    }
  }, [user]);

  // Auto-generate email when phone changes (for new users)
  useEffect(() => {
    console.log('🔄 Email auto-generation useEffect triggered:', {
      user: !!user,
      phone: formData.Phone,
      currentEmail: formData.Email,
      isEmailGenerated,
      shouldSkip: !!user
    });
    
    if (!user && formData.Phone && formData.Phone.trim() !== '') {
      // Only auto-generate if current email is empty or already generated
      if (!formData.Email || isEmailGenerated) {
        console.log('📧 AUTO-GENERATING EMAIL for new user');
        const tempEmail = generateTempEmail(formData.Phone);
        setFormData(prev => ({
          ...prev,
          Email: tempEmail
        }));
        setIsEmailGenerated(true);
      } else {
        console.log('📧 Skipping email generation - email already exists and not generated');
      }
    } else if (user) {
      console.log('📧 Skipping email generation - editing existing user');
    } else {
      console.log('📧 Skipping email generation - no phone or new user without phone');
    }
  }, [formData.Phone, user, isEmailGenerated]);

  // Load apartments when building ID is available OR when user data changes
  useEffect(() => {
    if (formData.BuildingId) {
      loadApartments();
    }
  }, [formData.BuildingId, user]); // Add 'user' to dependencies

 
  
  const loadApartments = async () => {
    try {
      
      // Use your existing apartments API
      const apartmentsData = await getListApartmensByBuildingId(formData.BuildingId);
      
      // Transform the data to match what the form expects
      // You may need to adjust the property names based on your API response structure
      const transformedApartments = apartmentsData.map(apt => ({
        ApartmentID: apt.apartmentID || apt.ApartmentID || apt.id,
        Number: apt.number || apt.Number || apt.apartmentNumber,
        Floor: apt.floor || apt.Floor || apt.floorNumber || 1
      }));

      // If editing a user and they have an apartment that's not in the API data, add it
      if (user && user.ApartmentID) {
        const userApartmentExists = transformedApartments.some(apt => apt.ApartmentID === user.ApartmentID);
        
        if (!userApartmentExists) {
          // Add the user's current apartment to ensure it appears in the dropdown
          transformedApartments.push({
            ApartmentID: user.ApartmentID,
            Number: user.ApartmentNumber || `${user.ApartmentID}`,
            Floor: user.FloorNumber || 1
          });
        }
      }

      // Sort apartments by number
      transformedApartments.sort((a, b) => {
        const numA = parseInt(a.Number);
        const numB = parseInt(b.Number);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
        return a.Number.localeCompare(b.Number);
      });

      setApartments(transformedApartments);
      
    } catch (error) {
      
      // Fallback: If there's an error and we're editing a user, at least include their apartment
      if (user && user.ApartmentID) {
        setApartments([{
          ApartmentID: user.ApartmentID,
          Number: user.ApartmentNumber || `${user.ApartmentID}`,
          Floor: user.FloorNumber || 1
        }]);
      } else {
        setApartments([]);
      }
    }
  };
  

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.FullName?.trim()) {
      newErrors.FullName = 'שם מלא הוא שדה חובה';
    }

    if (!formData.Phone?.trim()) {
      newErrors.Phone = 'מספר טלפון הוא שדה חובה';
    }

    // Apartment validation - now required
    if (!formData.ApartmentID) {
      newErrors.ApartmentID = 'בחירת דירה היא שדה חובה';
    }

    // Email validation - always validate since we'll have temp email
    if (formData.Email?.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.Email.trim())) {
        newErrors.Email = 'כתובת דוא"ל לא תקינה';
      }
    }

    // Phone validation - updated to work with formatted phone
    if (formData.Phone?.trim()) {
      const cleanPhone = getCleanPhoneNumber(formData.Phone);
      const phoneRegex = /^[0-9]{9,10}$/;
      if (!phoneRegex.test(cleanPhone)) {
        newErrors.Phone = 'מספר טלפון לא תקין (9-10 ספרות)';
      }
    }

    // Password validation for new users
    if (!user && !formData.PasswordHash?.trim()) {
      const newPassword = generatePassword();
      setFormData(prev => ({
        ...prev,
        PasswordHash: newPassword
      }));
    } else if (!user && formData.PasswordHash.length < 6) {
      newErrors.PasswordHash = 'סיסמה חייבת להכיל לפחות 6 תווים';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for phone field with formatting
    if (name === 'Phone') {
      const formattedPhone = formatPhoneNumber(value);
      setFormData(prev => ({
        ...prev,
        [name]: formattedPhone
      }));
    }
    // Special handling for email field
    else if (name === 'Email') {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      // Mark as manually entered if user types in email field
      setIsEmailGenerated(false);
    }
    // Special handling for relationship type change
    else if (name === 'RelationshipType') {
      setFormData(prev => {
        const newData = {
          ...prev,
          [name]: value
        };
        
        // If changing to 'בעלים', auto-fill tenant fields with user data
        if (value === 'בעלים') {
          newData.TenantName = prev.FullName || '';
          newData.TenantPhone = prev.Phone || '';
        }
        // If changing to 'שוכר', clear tenant fields
        else if (value === 'שוכר') {
          newData.TenantName = '';
          newData.TenantPhone = '';
        }
        
        return newData;
      });
    } 
    else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleGeneratePassword = () => {
    const newPassword = generatePassword();
    setFormData(prev => ({
      ...prev,
      PasswordHash: newPassword
    }));
    
    if (errors.PasswordHash) {
      setErrors(prev => ({
        ...prev,
        PasswordHash: ''
      }));
    }
  };

  const handleGenerateTempEmail = () => {
    console.log('🎯 handleGenerateTempEmail called manually');
    const tempEmail = generateTempEmail(formData.Phone);
    setFormData(prev => ({
      ...prev,
      Email: tempEmail
    }));
    setIsEmailGenerated(true);
    
    if (errors.Email) {
      setErrors(prev => ({
        ...prev,
        Email: ''
      }));
    }
  };

  // Auto-fill user data when email is entered and focus leaves the field
  const handleEmailBlur = async () => {
    const email = formData.Email?.trim();
    
    // Don't auto-fill if we're editing a user, email is empty, is a temp email, or is the same as previous
    if (user || !email || isTempEmail(email) || email === previousEmail) {
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return;
    }

    setIsAutoFilling(true);
    setPreviousEmail(email);

    try {
      // First, get user basic info by email to get userId
      const userData = await getCurrentProfile(email);
      
      if (userData && (userData.userID || userData.userId)) {
        const userId = userData.userID || userData.userId;
        const currentBuildingId = getCurrentBuildingId();
        
        // Get user buildings to find role info for current building
        const userBuildings = await getUserBuildings(userId);
        
        // Find the current building in user's buildings
        const currentBuildingData = userBuildings.find(building => 
          building.buildingId === currentBuildingId
        );

        // If user is not associated with current building, don't auto-fill
        if (!currentBuildingData) {
          alert(`המשתמש ${userData.fullName || userData.FullName} לא קשור לבניין הנוכחי`);
          return;
        }

        // Auto-fill the form with the fetched user data and building-specific roles
        setFormData(prev => ({
          ...prev,
          FullName: userData.fullName || userData.FullName || '',
          Phone: formatPhoneNumber(userData.phone || userData.Phone || ''),
          IsManager: currentBuildingData.isManager === 'כן' ? 'כן' : 'לא',
          IsVaad: currentBuildingData.isVaad === 'כן' ? 'כן' : 'לא',
          IsTenant: currentBuildingData.isTenant === 'כן' ? 'כן' : 'לא',
          RelationshipType: userData.relationshipType || userData.RelationshipType || 'שוכר',
          ApartmentNumber: userData.apartmentNumber || userData.ApartmentNumber || '',
          FloorNumber: userData.floorNumber || userData.FloorNumber || '',
          OwnerName: userData.ownerName || userData.OwnerName || '',
          OwnerPhone: userData.ownerPhone || userData.OwnerPhone || '',
          TenantName: userData.tenantName || userData.TenantName || '',
          TenantPhone: userData.tenantPhone || userData.TenantPhone || '',
          VaadMonthly: userData.vaadMonthly || userData.VaadMonthly || 0,
          ApartmentID: userData.apartmentID || userData.ApartmentID || null
        }));

        setIsEmailGenerated(false);
        
        // Show success message with building-specific role info
        const roleText = currentBuildingData.isManager === 'כן' && currentBuildingData.isVaad === 'כן' 
          ? 'מנהל וחבר ועד' 
          : currentBuildingData.isManager === 'כן' 
            ? 'מנהל' 
            : currentBuildingData.isVaad === 'כן' 
              ? 'חבר ועד' 
              : currentBuildingData.isTenant === 'כן' 
                ? 'דייר' 
                : 'משתמש';
        
        alert(`נתוני המשתמש נטענו אוטומטית עבור: ${userData.fullName || userData.FullName} (${roleText})`);
      }
    } catch (error) {
      // Don't show error for user not found - this is expected for new users
      console.log('User not found for email:', email, error.message);
    } finally {
      setIsAutoFilling(false);
    }
  };

  const handleApartmentChange = (e) => {
    const apartmentId = parseInt(e.target.value);
    const selectedApartment = apartments.find(apt => apt.ApartmentID === apartmentId);
    
    
    setFormData(prev => ({
      ...prev,
      ApartmentID: apartmentId || null,
      ApartmentNumber: selectedApartment?.Number || '',
      FloorNumber: selectedApartment?.Floor || ''
    }));

    // Clear apartment error when user selects an apartment
    if (errors.ApartmentID) {
      setErrors(prev => ({
        ...prev,
        ApartmentID: ''
      }));
    }
  };

 
  // Fixed handleSubmit function for UserForm.jsx
// Updated handleSubmit function for UserForm.jsx
const handleSubmit = async (e) => {
  e.preventDefault();
  
  // Ensure we have an email (generate temp if needed) - ONLY FOR NEW USERS
  let finalFormData = { ...formData };
  
  // Only auto-generate email for NEW users, not when editing existing users
  if (!user && (!finalFormData.Email || finalFormData.Email.trim() === '')) {
    finalFormData.Email = generateTempEmail(finalFormData.Phone);
    setIsEmailGenerated(true);
    console.log('📧 Auto-generated email for new user:', finalFormData.Email);
  } else if (user) {
    console.log('📧 Editing existing user - preserving email as is:', finalFormData.Email || '(empty)');
  }
  
  // Auto-generate password if empty for new users
  if (!user && !finalFormData.PasswordHash?.trim()) {
    finalFormData.PasswordHash = generatePassword();
  }
  
  // Update form data with generated values
  setFormData(finalFormData);
  
  if (!validateForm()) {
    return;
  }

  setLoading(true);
  try {
    // Clean phone number
    const cleanPhone = getCleanPhoneNumber(finalFormData.Phone);

    // Prepare user data with proper formatting for backend
    const userData = {
      // Core user fields
      FullName: finalFormData.FullName?.trim(),
      Email: finalFormData.Email?.trim(),
      Phone: cleanPhone,

      // Password (only for new users or when explicitly changed)
      ...((!user || finalFormData.PasswordHash?.trim()) && {
        PasswordHash: finalFormData.PasswordHash?.trim()
      }),

      // Building and apartment information
      BuildingId: finalFormData.BuildingId,
      ApartmentID: finalFormData.ApartmentID,
      ApartmentNumber: finalFormData.ApartmentNumber,
      FloorNumber: parseInt(finalFormData.FloorNumber) || null,

      // Parking and Charging Station
      ParkingNumber: finalFormData.ParkingNumber || '',
      ChargingStation: finalFormData.ChargingStation,

      // Relationship and financial info
      RelationshipType: finalFormData.RelationshipType,
      OwnerName: finalFormData.OwnerName || null,
      OwnerPhone: finalFormData.OwnerPhone || null,
      TenantName: finalFormData.TenantName || null,
      TenantPhone: finalFormData.TenantPhone || null,
      VaadMonthly: parseInt(finalFormData.VaadMonthly) || 0,

      // Role permissions - send as Hebrew strings (conversion handled in fetchUser)
      IsManager: finalFormData.IsManager, // Will be converted to Hebrew in fetchUser
      IsVaad: finalFormData.IsVaad,
      IsTenant: finalFormData.IsTenant,

      // Include UserID for updates
      ...(user && { UserID: user.UserID }),

      // Add empty _users array to satisfy backend requirement
      _users: []
    };

    // Remove undefined values (but keep null values and don't remove OwnerName/OwnerPhone/TenantName/TenantPhone)
    Object.keys(userData).forEach(key => {
      if (userData[key] === undefined || (userData[key] === '' && key !== 'Email' && key !== 'OwnerName' && key !== 'OwnerPhone' && key !== 'TenantName' && key !== 'TenantPhone')) {
        delete userData[key];
      }
    });



    let result;
    try {
      if (user) {
        // For updates, use the management-specific function
        result = await updateUserBuildingRole(userData);
      } else {
        // For new users, use createUserWithBuilding
        result = await createUserWithBuilding(userData);
      }
      
      
      // Check if the result indicates success
      if (result && (result.success !== false)) {
        onSave(result);
      } else {
        throw new Error(result?.message || 'שגיאה בשמירת הדייר');
      }
      
    } catch (apiError) {
      throw apiError; // Re-throw to be caught by outer catch
    }

  } catch (error) {
    
    // Set user-friendly error messages
    let displayError = 'שגיאה בשמירת הדייר. אנא נסה שוב.';
    
    if (error.message) {
      // Check for specific error types
      if (error.message.includes('Email already exists') || error.message.includes('דוא"ל כבר קיים')) {
        displayError = 'דוא"ל זה כבר קיים במערכת. אנא השתמש בדוא"ל אחר.';
      } else if (error.message.includes('Phone already exists') || error.message.includes('טלפון כבר קיים')) {
        displayError = 'מספר טלפון זה כבר קיים במערכת. אנא השתמש במספר אחר.';
      } else if (error.message.includes('Network error') || error.message.includes('Failed to fetch')) {
        displayError = 'שגיאת רשת. אנא בדוק את החיבור לאינטרנט ונסה שוב.';
      } else if (error.message.includes('_users field is required')) {
        displayError = 'שגיאה בפורמט הנתונים. אנא נסה שוב.';
      } else if (error.message.includes('could not be converted to System.String')) {
        displayError = 'שגיאה בפורמט נתוני התפקידים. אנא נסה שוב.';
      } else {
        displayError = error.message;
      }
    }
    
    setErrors({ 
      general: displayError
    });
  } finally {
    setLoading(false);
  }
};

  // Helper function to check if field is required
  const isFieldRequired = (fieldName) => {
    switch (fieldName) {
      case 'FullName':
      case 'Phone':
      case 'ApartmentID':  // Added apartment as required field
        return true;
      case 'PasswordHash':
        return !user;
      default:
        return false;
    }
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Mobile update button (rendered via portal)
  const mobileUpdateButton = isMobile && user && createPortal(
    <button
      type="button"
      className={`${styles.saveButton} ${styles.updateButton} ${styles.mobileFloatingButton}`}
      onClick={(e) => {
        e.preventDefault();
        handleSubmit(e);
      }}
      disabled={loading}
    >
      <Check size={20} />
    </button>,
    document.body
  );

  // Mobile cancel button (rendered via portal)
  const mobileCancelButton = isMobile && createPortal(
    <button
      type="button"
      className={`${styles.cancelButton} ${styles.mobileCancelButton}`}
      onClick={onCancel}
      disabled={loading}
    >
      <X size={20} />
    </button>,
    document.body
  );

  // Mobile header (rendered via portal)
  const mobileHeader = isMobile && createPortal(
    <div className={`${styles.formHeader} ${styles.mobileFormHeader}`}>
      <button
        className={styles.backButton}
        onClick={onCancel}
        type="button"
        aria-label="חזור"
      >
        <ChevronRight size={20} />
      </button>
      <h2>{user ? 'עריכת דייר' : 'הוספת דייר חדש'}</h2>
    </div>,
    document.body
  );

  return (
    <>
    {mobileHeader}
    <div className={styles.userFormContainer}>
      {/* Form Header - Hidden on mobile (rendered via portal) */}
      {!isMobile && (
        <div className={styles.formHeader}>
          <button
            className={styles.backButton}
            onClick={onCancel}
            type="button"
            aria-label="חזור"
          >
            <ChevronRight size={20} />
          </button>
          <h2>{user ? 'עריכת דייר' : 'הוספת דייר חדש'}</h2>
        </div>
      )}

      <form className={styles.userForm} onSubmit={handleSubmit} autoComplete="off">
        {/* Honeypot fields to confuse autofill */}
        <input 
          type="email" 
          name="email" 
          tabIndex="-1" 
          style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}
          autoComplete="email"
        />
        <input 
          type="password" 
          name="password" 
          tabIndex="-1" 
          style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}
          autoComplete="current-password"
        />
        
        {errors.general && (
          <div className={styles.errorMessage} role="alert">
            {errors.general}
          </div>
        )}

        {/* Personal Information Section */}
        <div className={styles.formSection}>
          <h3 tabIndex="0">
            <User size={18} style={{ marginLeft: '8px' }} />
            פרטים אישיים
          </h3>
          
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="fullName">
                שם מלא {isFieldRequired('FullName') && '*'}
              </label>
              <input
                id="fullName"
                type="text"
                name="FullName"
                value={formData.FullName}
                onChange={handleInputChange}
                className={errors.FullName ? styles.error : ''}
                placeholder="הכנס שם מלא"
                required={isFieldRequired('FullName')}
                aria-describedby={errors.FullName ? "fullName-error" : undefined}
              />
              {errors.FullName && (
                <div id="fullName-error" className={styles.fieldError} role="alert">
                  {errors.FullName}
                </div>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="phone">
                טלפון {isFieldRequired('Phone') && '*'}
              </label>
              <input
                id="phone"
                type="tel"
                name="Phone"
                value={formData.Phone}
                onChange={handleInputChange}
                className={errors.Phone ? styles.error : ''}
                placeholder="050-1234567"
                required={isFieldRequired('Phone')}
                aria-describedby={errors.Phone ? "phone-error" : "phone-help"}
              />
              {errors.Phone && (
                <div id="phone-error" className={styles.fieldError} role="alert">
                  {errors.Phone}
                </div>
              )}
              <small id="phone-help" style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                הקלד מספר טלפון - הפורמט יתווסף אוטומטית (050-1234567)
              </small>
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="email">
                דוא"ל {isEmailGenerated ? '(נוצר אוטומטית)' : ''}
                {!user && (
                  <button
                    type="button"
                    className={styles.togglePassword}
                    onClick={handleGenerateTempEmail}
                    aria-label="יצירת דוא״ל זמני"
                    title="יצירת דוא״ל זמני חדש"
                  >
                    <RefreshCw size={16} />
                  </button>
                )}
              </label>
              <input
                id="email"
                type="email"
                name="Email"
                data-form-type="other"
                value={formData.Email}
                onChange={handleInputChange}
                onBlur={handleEmailBlur}
                className={errors.Email ? styles.error : ''}
                placeholder="user@example.com"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                readOnly={false}
                aria-describedby={errors.Email ? "email-error" : "email-help"}
                disabled={isAutoFilling}
              />
              {isAutoFilling && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  marginTop: '8px', 
                  fontSize: '14px', 
                  color: '#666' 
                }}>
                  <Loader size={16} className={styles.loadingSpinner} />
                  טוען נתוני משתמש...
                </div>
              )}
              {errors.Email && (
                <div id="email-error" className={styles.fieldError} role="alert">
                  {errors.Email}
                </div>
              )}
              <small id="email-help" style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                {isEmailGenerated 
                  ? 'דוא"ל זמני נוצר אוטומטית. ניתן לערוך או ליצור חדש.'
                  : user 
                    ? 'הכנס דוא"ל אמיתי או השאר ריק ליצירת דוא"ל זמני'
                    : 'הכנס דוא"ל קיים לטעינה אוטומטית של נתוני המשתמש, או השאר ריק ליצירת דוא"ל זמני'
                }
              </small>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="password">
                {user ? 'סיסמה חדשה (אופציונלי)' : `סיסמה ${isFieldRequired('PasswordHash') ? '*' : ''}`}
                <div style={{ display: 'flex', gap: '8px', marginRight: 'auto' }}>
                  <button
                    type="button"
                    className={styles.togglePassword}
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'הסתר סיסמה' : 'הצג סיסמה'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                  {!user && (
                    <button
                      type="button"
                      className={styles.togglePassword}
                      onClick={handleGeneratePassword}
                      aria-label="יצירת סיסמה חדשה"
                      title="יצירת סיסמה חדשה"
                    >
                      <RefreshCw size={16} />
                    </button>
                  )}
                </div>
              </label>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                name="PasswordHash"
                data-form-type="other"
                value={formData.PasswordHash}
                onChange={handleInputChange}
                className={errors.PasswordHash ? styles.error : ''}
                placeholder={user ? 'השאר ריק כדי לא לשנות' : 'סיסמה נוצרה אוטומטית'}
                required={isFieldRequired('PasswordHash')}
                autoComplete="new-password"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                readOnly={false}
                minLength={!user ? 6 : undefined}
                aria-describedby={errors.PasswordHash ? "password-error" : undefined}
              />
              {errors.PasswordHash && (
                <div id="password-error" className={styles.fieldError} role="alert">
                  {errors.PasswordHash}
                </div>
              )}
              {!user && (
                <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                  סיסמה נוצרה אוטומטית. לחץ על כפתור הרענון ליצירת סיסמה חדשה.
                </small>
              )}
            </div>
          </div>
        </div>

        {/* Apartment Information Section */}
        <div className={styles.formSection}>
          <h3 tabIndex="0">
            <Home size={18} style={{ marginLeft: '8px' }} />
            פרטי דירה
          </h3>
          
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="apartment">
                דירה {isFieldRequired('ApartmentID') && '*'}
              </label>
              <select
                id="apartment"
                name="ApartmentID"
                value={formData.ApartmentID || ''}
                onChange={handleApartmentChange}
                className={errors.ApartmentID ? styles.error : ''}
                required={isFieldRequired('ApartmentID')}
                aria-describedby={errors.ApartmentID ? "apartment-error" : undefined}
              >
                <option value="">בחר דירה</option>
                {apartments.map(apt => (
                  <option key={apt.ApartmentID} value={apt.ApartmentID}>
                    דירה {apt.Number}
                    {formData.ApartmentID === apt.ApartmentID && ' (נוכחי)'}
                  </option>
                ))}
              </select>
              {errors.ApartmentID && (
                <div id="apartment-error" className={styles.fieldError} role="alert">
                  {errors.ApartmentID}
                </div>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="relationshipType">סוג קשר לדירה</label>
              <select
                id="relationshipType"
                name="RelationshipType"
                value={formData.RelationshipType}
                onChange={handleInputChange}
              >
                <option value="בעלים">בעלים</option>
                <option value="שוכר">שוכר</option>
              </select>
            </div>
          </div>

          {/* Owner Information - Show only when RelationshipType is 'שוכר' */}
          {formData.RelationshipType === 'שוכר' && (
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="ownerName">שם בעלים</label>
                <input
                  id="ownerName"
                  type="text"
                  name="OwnerName"
                  value={formData.OwnerName}
                  onChange={handleInputChange}
                  placeholder="שם מלא של הבעלים"
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="ownerPhone">טלפון בעלים</label>
                <input
                  id="ownerPhone"
                  type="tel"
                  name="OwnerPhone"
                  value={formData.OwnerPhone}
                  onChange={handleInputChange}
                  placeholder="05X-XXX-XXXX"
                />
              </div>
            </div>
          )}

          {/* Tenant Information - Show only when RelationshipType is 'בעלים' */}
          {formData.RelationshipType === 'בעלים' && (
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="tenantName">שם דייר</label>
                <input
                  id="tenantName"
                  type="text"
                  name="TenantName"
                  value={formData.TenantName}
                  onChange={handleInputChange}
                  placeholder="שם מלא של הדייר (מתמלא אוטומטית)"
                />
                <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                  מתמלא אוטומטית מפרטי המשתמש כשהוא בעלים
                </small>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="tenantPhone">טלפון דייר</label>
                <input
                  id="tenantPhone"
                  type="tel"
                  name="TenantPhone"
                  value={formData.TenantPhone}
                  onChange={handleInputChange}
                  placeholder="05X-XXX-XXXX (מתמלא אוטומטית)"
                />
                <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                  מתמלא אוטומטית מפרטי המשתמש כשהוא בעלים
                </small>
              </div>
            </div>
          )}

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="floorNumber">קומה</label>
              <input
                id="floorNumber"
                type="number"
                name="FloorNumber"
                value={formData.FloorNumber}
                onChange={handleInputChange}
                min="0"
                max="50"
                placeholder="מספר קומה"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="vaadMonthly">דמי ועד חודשיים (₪)</label>
              <input
                id="vaadMonthly"
                type="number"
                name="VaadMonthly"
                value={formData.VaadMonthly}
                onChange={handleInputChange}
                min="0"
                step="10"
                placeholder="סכום בשקלים"
              />
            </div>
          </div>

          {/* Parking and Charging Station */}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="parkingNumber">מספרי חניה (מופרדים בפסיק)</label>
              <input
                id="parkingNumber"
                type="text"
                name="ParkingNumber"
                value={formData.ParkingNumber}
                onChange={handleInputChange}
                placeholder="למשל: 1, 2, 3"
              />
              <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                ניתן להכניס מספר חניות מופרדות בפסיק
              </small>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="chargingStation">עמדת טעינה</label>
              <select
                id="chargingStation"
                name="ChargingStation"
                value={formData.ChargingStation ? 'true' : 'false'}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    ChargingStation: e.target.value === 'true'
                  }));
                }}
              >
                <option value="false">לא</option>
                <option value="true">כן</option>
              </select>
            </div>
          </div>
        </div>

        {/* Permissions Section */}
        <div className={styles.formSection}>
          <h3 tabIndex="0">
            <Shield size={18} style={{ marginLeft: '8px' }} />
            הרשאות במערכת
          </h3>
          
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="isManager">מנהל</label>
              <select
                id="isManager"
                name="IsManager"
                value={formData.IsManager}
                onChange={handleInputChange}
              >
                <option value="לא">לא</option>
                <option value="כן">כן</option>
              </select>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="isVaad">חבר ועד</label>
              <select
                id="isVaad"
                name="IsVaad"
                value={formData.IsVaad}
                onChange={handleInputChange}
              >
                <option value="לא">לא</option>
                <option value="כן">כן</option>
              </select>
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="isTenant">דייר</label>
              <select
                id="isTenant"
                name="IsTenant"
                value={formData.IsTenant}
                onChange={handleInputChange}
              >
                <option value="לא">לא</option>
                <option value="כן">כן</option>
              </select>
            </div>
            <div className={styles.formGroup}>
              {/* Empty div for grid layout */}
            </div>
          </div>
        </div>

        {/* Contact Method Summary */}
        <div className={styles.formSection} style={{ backgroundColor: '#f8fafc', padding: '16px', marginBottom: '24px' }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#374151' }}>
            סיכום נתוני דייר:
          </h4>
          <div style={{ fontSize: '13px', color: '#6b7280' }}>
            {formData.Phone?.trim() && (
              <div>📱 טלפון: {formData.Phone}</div>
            )}
            {formData.Email?.trim() && (
              <div style={{ color: isEmailGenerated ? '#f59e0b' : '#059669' }}>
                📧 דוא"ל: {formData.Email} 
                {isEmailGenerated && <span style={{ fontWeight: 'bold' }}> (זמני)</span>}
              </div>
            )}
            {!user && formData.PasswordHash && (
              <div style={{ marginTop: '8px', fontWeight: 'bold', color: '#059669' }}>
                🔑 סיסמה: {showPassword ? formData.PasswordHash : '••••••••'}
              </div>
            )}
          </div>
        </div>

        {/* Form Actions */}
        <div className={styles.formActions}>
          {/* Hide cancel button on mobile (will be rendered via portal) */}
          {!isMobile && (
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onCancel}
              disabled={loading}
            >
              ביטול
            </button>
          )}
          {/* Hide update button on mobile when editing (will be rendered via portal) */}
          {!(isMobile && user) && (
            <button
              type="submit"
              className={`${styles.saveButton} ${user ? styles.updateButton : ''}`}
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className={styles.loadingSpinner} style={{ width: '16px', height: '16px' }}></div>
                  שומר...
                </>
              ) : user ? (
                <>
                  <Check size={16} />
                  <span className={styles.buttonTextDesktop}>עדכן דייר</span>
                </>
              ) : (
                <>
                  <Save size={16} />
                  צור דייר
                </>
              )}
            </button>
          )}
        </div>
      </form>
    </div>
    {mobileCancelButton}
    {mobileUpdateButton}
    </>
  );
};

export default UserForm;